import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { MCDataQuestion, MCTestDataDto } from '../DomainModels';
import { useI18n } from "src/utils/lni18n";
import HtmlTextEditor from 'src/components/TextEditor/HtmlTextEditor';
import LnIcon from 'src/components/LnIcon';
import { MCTestService } from './MCTestService';
import { useConfirm } from 'src/components/Modalservice/UseConfirm';

const createEmptyData = (): MCDataQuestion => ({
    Question: "",
    QuestionError: undefined,
    McAnswers: [],
    CanSelectMultipleAnswers: false,
    Reply: undefined,
    Checked: false,
    Index: 0
});


const QuestionEditor = (props: { readOnly: boolean, data: MCDataQuestion, onChange: (d: MCTestDataDto) => void, service: MCTestService }) => {
    const { languageService: t } = useI18n();
    const { data, onChange, service, readOnly } = props;
    const [editData, setEditData] = useState<MCDataQuestion | null>(null);
    const confirm = useConfirm();




    const updateLocalData = useCallback((newdata: MCDataQuestion) => {
        setEditData({ ...newdata, McAnswers: [...newdata.McAnswers] });
    }, []);


    useEffect(() => {
        // Reset to empty state first if needed for animation
        setEditData(createEmptyData());

        // Use requestAnimationFrame for next paint update
        const handle = requestAnimationFrame(() => {
            updateLocalData(data);
        });

        return () => cancelAnimationFrame(handle);

    }, [data.Index, updateLocalData]);

    const updateAndRefreshLocalData = useCallback((newData: MCTestDataDto) => {
        onChange(newData);
        const checkedQuestion = newData.Questions.find(q => q.Checked);
        if (checkedQuestion) {
            updateLocalData(checkedQuestion);
        }
    }, [onChange, updateLocalData]);

    function handleQuestionChange(e: ChangeEvent<HTMLTextAreaElement>): void {
        const data = service.updateQuestionText(e.currentTarget.value);
        onChange(data);
    }

    function handleAnswerChange(e: ChangeEvent<HTMLTextAreaElement>, index: number) {
        const data = service.updateAnswerText(e.currentTarget.value, index);
        onChange(data);
    }

    function handleAnswerCorrect(e: ChangeEvent<HTMLInputElement>, index: number) {
        const newData = service.updateAnswerIsCorrect(e.currentTarget.checked, index);
        updateAndRefreshLocalData(newData);
    }

    function addAnswer() {
        const newData = service.addAnswer();
        updateAndRefreshLocalData(newData);
    }

    function setCanSelectMultipleAnswers(e: ChangeEvent<HTMLInputElement>): void {
        const newData = service.setCanSelectMultipleAnswers(e.currentTarget.checked)
        updateAndRefreshLocalData(newData);
    }

    function deleteAnswer(index: number) {

        confirm("md", t.getText("delete"),
            <div className="preserve-white" >{t.getText("delete.answeroption")}</div>)
            .then((res: boolean) => {
                if (res) {
                    const newData = service.removeAnswer(index);
                    updateAndRefreshLocalData(newData);
                }
            }).catch((e: Error) => { });


    }
    if (!data) return null;

    if (!editData) return null;
    const numAnswers = editData.McAnswers.length;
    const numAnswersCorrect = editData.McAnswers.filter(a => a.IsCorrect).length;

    return <div className="p-5">


        <div className='mb-3'>
            <label className="fw-bold pointer">
                <input disabled={readOnly} name="CanSelectMultipleAnswers" checked={editData.CanSelectMultipleAnswers} onChange={setCanSelectMultipleAnswers} type="checkbox" className='me-3 pointer' />
                {t.getText("can.select.multiple.answers")}
            </label>
        </div>


        <label className="fw-bold">{t.getText("question")}</label>
        {editData.QuestionError && <span className="ms-4 text-danger fw-bold">{editData.QuestionError}</span>}
        <HtmlTextEditor key={`question${editData.Index}`} readOnly={readOnly} onChange={handleQuestionChange} value={""} initialValue={editData.Question}
            name="Question"
            className={`${editData.QuestionError ? 'border border-danger' : ''}`}  ></HtmlTextEditor>


        <div className="mt-4">
            <div className='d-flex mb-2'>
                <label className="fw-bold">{t.getText("options")}</label>
                {!readOnly && <div className='d-flex ms-auto align-items-center'>
                    <button disabled={readOnly} onClick={addAnswer} className='btn btn-primary btn-small'>{t.getText("add")}</button>
                    <span className="ms-3">
                        ( {t.getText("numofanswers")} {editData.McAnswers?.length ?? 0})
                    </span>
                </div>
                }
            </div>

            <div key={`answer${editData.Index}`}>

                {editData.McAnswers.map((answer, index) => {

                    const blockChangeCorrect = readOnly
                        || editData.CanSelectMultipleAnswers && numAnswersCorrect === 1 && answer.IsCorrect;

                    return <div key={index} className='mb-3'>

                        <div className='d-flex'>
                            <div className='me-3'>
                                {(!editData.CanSelectMultipleAnswers && index === 0) && <input disabled={readOnly} type="radio" defaultChecked value="true" />}
                                {(!editData.CanSelectMultipleAnswers && index > 0) && <input disabled type="radio" />}
                                {editData.CanSelectMultipleAnswers && <input disabled={blockChangeCorrect} type="checkbox" checked={answer.IsCorrect} onChange={(e) => handleAnswerCorrect(e, index)} />}

                            </div>
                            <div className='flex-fill d-flex'>
                                <div className='flex-fill '>
                                    <div>
                                        <strong>{answer.IsCorrect ? t.getText("correct") : t.getText("error")}</strong>
                                        {answer.Error && <span className="ms-4 text-danger fw-bold">{answer.Error}</span>}
                                    </div>
                                    <HtmlTextEditor readOnly={readOnly} name={`McAnswers.${index}.Text`} className={`${answer.IsCorrect ? 'border border-primary' : ''} ${answer.Error ? 'border border-danger' : ''}`}
                                        onChange={(e) => handleAnswerChange(e, index)}
                                        value={""} initialValue={editData.McAnswers[index]?.Text || ""} />
                                </div>

                                {(numAnswers > 2) && <button disabled={readOnly} className="btn btn-link ms-3 mt-4" type="button" onClick={() => deleteAnswer(index)}>
                                    <LnIcon name="delete" className='icon-small' />
                                </button>}
                            </div>

                        </div>
                    </div>
                })}

            </div>
        </div>



    </div >

}

export default QuestionEditor;

